import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";

import { CardLayout, DeleteModal, MTable, ActionButton } from "../v3";
import { TableActions } from "../v3";
import { HumanResourceService } from "../../services/hr.service";
import { AddEditParticular } from "./AddEditParticular.component";

// ─── Column definitions ───────────────────────────────────────────────────────

const getColumns = (handleActions: (row: any, action: string) => void) => [
  { field: "sl", headerName: "Sl No", flex: 0.4, sortable: false },
  { field: "name", headerName: "Item Name", flex: 2, sortable: false },
  {
    field: "defaultRate",
    headerName: "Default Rate",
    flex: 1.5,
    sortable: false,
    renderCell: (params: any) => {
      const val = Number(params.value);
      return isNaN(val) ? "-" : `₹ ${val.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
  },
  {
    field: "actions",
    headerName: "Actions",
    flex: 0.8,
    sortable: false,
    renderCell: (params: any) => (
      <TableActions
        item={params}
        actions={[
          { icon: Edit, type: "edit", label: "Edit", onClick: handleActions },
          { icon: Delete, type: "delete", label: "Delete", onClick: handleActions },
        ]}
      />
    ),
  },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface Particular {
  id: number;
  name: string;
  defaultRate: number;
}

interface FormState {
  name: string;
  defaultRate: string;
}

interface Props {
  workerName?: string;
  onWorkerNameLoaded?: (name: string) => void;
}

const emptyForm: FormState = { name: "", defaultRate: "" };

// ─── Component ────────────────────────────────────────────────────────────────

export const WorkerParticulars = ({ workerName, onWorkerNameLoaded }: Props) => {
  const { id } = useParams<{ id: string }>();
  const workerId = Number(id); // convert string → number for API

  const [rows, setRows] = useState<Particular[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationState, setPaginationState] = useState({ page: 0, pageSize: 10 });
  const [searchText, setSearchText] = useState("");

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  // Delete modal
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: number | null }>({
    isOpen: false,
    id: null,
  });

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!workerId || isNaN(workerId)) return;
    setLoading(true);
    try {
      const res = await HumanResourceService.getWorkerParticulars({
        workerId,
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        ...(searchText ? { name: searchText } : {}),
      });
      const data = res?.data;
      const items: Particular[] = data?.rows ?? data ?? [];
      setRows(items);
      setTotalCount(data?.count ?? items.length);

      // resolve worker name once
      if (!workerName && onWorkerNameLoaded) {
        try {
          const empRes = await HumanResourceService.getEmployeeById(workerId);
          if (empRes?.data?.name) onWorkerNameLoaded(empRes.data.name);
        } catch (_e) {
          /* ignore */
        }
      }
    } catch (_e) {
      toast.error("Failed to load particulars");
    } finally {
      setLoading(false);
    }
  }, [workerId, paginationState, searchText, workerName, onWorkerNameLoaded]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Table row mapping ───────────────────────────────────────────────────────

  const tableRows = rows.map((item, idx) => ({
    ...item,
    sl: idx + 1 + paginationState.page * paginationState.pageSize,
  }));

  // ─── Actions ─────────────────────────────────────────────────────────────────

  const handleActions = (row: Particular, action: string) => {
    if (action === "edit") {
      setEditingId(row.id);
      setForm({ name: row.name, defaultRate: String(row.defaultRate) });
      setFormOpen(true);
    } else if (action === "delete") {
      setDeleteModal({ isOpen: true, id: row.id });
    }
  };

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  // ─── Delete ───────────────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    const { id: delId } = deleteModal;
    setDeleteModal({ isOpen: false, id: null });
    try {
      await HumanResourceService.deleteWorkerParticular(delId!);
      toast.success("Particular deleted successfully");
      fetchData();
    } catch (_e) {
      toast.error("Error deleting particular");
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  const columns = getColumns(handleActions);

  return (
    <>
      <CardLayout>
        <MTable
          // Search
          showSearchBar
          placeholder="Search by item name"
          searchText={searchText}
          onSearchChange={(val: string) => {
            setSearchText(val);
            setPaginationState((p) => ({ ...p, page: 0 }));
          }}
          // Table
          columnHeaders={columns}
          tableRows={tableRows}
          rowCount={totalCount}
          loading={loading}
          paginationModel={paginationState}
          onPaginationChange={setPaginationState}
          // Custom toolbar slot — Add button in search bar area
          showExportButton={false}
          style={{}}
        />

        {/* Floating Add button aligned right of the MTable toolbar */}
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <ActionButton buttonText="Add Particular" onButtonClick={handleOpenAdd} />
        </Box>
      </CardLayout>

      {/* ─── Add / Edit Dialog (Reused Component) ─────────────────────────── */}
      <AddEditParticular
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={() => fetchData()}
        workerId={workerId}
        editId={editingId}
        initialData={form}
      />

      {/* ─── Delete Modal ──────────────────────────────────────────────── */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: null })}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this particular?"
      />
    </>
  );
};
